export type TAuthTokenSource = "login" | "2fa" | "device" | "stored";

type TSessionDebugState = {
	lastAuthToken: string | null;
	lastAuthTokenAt: string | null;
	lastAuthTokenSource: TAuthTokenSource | null;
	lastReplacedToken: string | null;
	lastReplacedTokenAt: string | null;
};

export const sessionDebug: TSessionDebugState = {
	lastAuthToken: null,
	lastAuthTokenAt: null,
	lastAuthTokenSource: null,
	lastReplacedToken: null,
	lastReplacedTokenAt: null,
};

export const recordAuthToken = (token: string, source: TAuthTokenSource): void => {
	sessionDebug.lastAuthToken = token;
	sessionDebug.lastAuthTokenAt = new Date().toISOString();
	sessionDebug.lastAuthTokenSource = source;
};

export const recordReplacedToken = (token: string): void => {
	sessionDebug.lastReplacedToken = token;
	sessionDebug.lastReplacedTokenAt = new Date().toISOString();
};

export const maskToken = (token: string | null | undefined): string | null => {
	if (!token) return null;
	if (token.length <= 16) return `${token} (len ${token.length})`;
	return `${token.slice(0, 10)}…${token.slice(-8)} (len ${token.length})`;
};

export const readStoredAuthToken = (): string | null => {
	try {
		const raw = localStorage.getItem("auth");
		if (!raw) return null;

		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return null;

		const data = (parsed as { data?: unknown }).data;
		if (!data || typeof data !== "object") return null;

		const token = (data as { token?: unknown }).token;
		return typeof token === "string" ? token : null;
	} catch {
		return null;
	}
};

const sameToken = (a: string | null | undefined, b: string | null | undefined): boolean | null => {
	if (!a || !b) return null;
	return a === b;
};

export type TSessionTerminationDebug = {
	failedRequestMethod: string | null;
	failedRequestToken: string | null;
	recoveryToken: string | null;
	recoveryStatus: number | null;
	fingerprint: string | null;
};

export const buildSessionTerminationDebug = (debug: Partial<TSessionTerminationDebug>, storedToken: string | null) => ({
	failed_request: {
		method: debug.failedRequestMethod ?? null,
		token_sent: maskToken(debug.failedRequestToken),
	},
	recovery: {
		token_sent: maskToken(debug.recoveryToken),
		http_status: debug.recoveryStatus ?? null,
		fingerprint: debug.fingerprint ?? null,
	},
	auth: {
		last_token: maskToken(sessionDebug.lastAuthToken),
		received_at: sessionDebug.lastAuthTokenAt,
		source: sessionDebug.lastAuthTokenSource,
		last_replaced_token: maskToken(sessionDebug.lastReplacedToken),
		replaced_at: sessionDebug.lastReplacedTokenAt,
	},
	local_storage_token: maskToken(storedToken),
	checks: {
		failed_token_matches_last_auth_token: sameToken(debug.failedRequestToken, sessionDebug.lastAuthToken),
		failed_token_matches_stored_token: sameToken(debug.failedRequestToken, storedToken),
		last_auth_token_matches_stored_token: sameToken(sessionDebug.lastAuthToken, storedToken),
	},
});
